import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Users as UsersIcon, Search, Phone, Mail, Package, TrendingUp } from "lucide-react";
import { api, peso, formatApiError } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/users/customers")
      .then((r) => setCustomers(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return customers;
    return customers.filter((c) =>
      (c.name || "").toLowerCase().includes(query) ||
      (c.email || "").toLowerCase().includes(query) ||
      (c.phone || "").toLowerCase().includes(query)
    );
  }, [customers, q]);

  const totalSpent = customers.reduce((s, c) => s + (c.total_spent || 0), 0);
  const withOrders = customers.filter((c) => c.order_count > 0).length;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-heading font-bold text-xl sm:text-2xl text-[#1C2826]">Registered Customers</h2>
        <p className="text-sm text-[#4A5D5A]">Everyone who has created an account with their email and mobile number.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-[#E1E5DF] rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-[#738380] flex items-center gap-1"><UsersIcon className="h-3.5 w-3.5" /> Customers</div>
          <div className="font-heading font-extrabold text-2xl text-[#1C2826]" data-testid="customer-count">{customers.length}</div>
        </div>
        <div className="bg-white border border-[#E1E5DF] rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-[#738380] flex items-center gap-1"><Package className="h-3.5 w-3.5" /> Have ordered</div>
          <div className="font-heading font-extrabold text-2xl text-[#2F5233]">{withOrders}</div>
        </div>
        <div className="bg-white border border-[#E1E5DF] rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-[#738380] flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" /> Lifetime sales</div>
          <div className="font-heading font-extrabold text-2xl text-[#E67E22]">{peso(totalSpent)}</div>
        </div>
        <div className="bg-white border border-[#E1E5DF] rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-[#738380]">Signed up today</div>
          <div className="font-heading font-extrabold text-2xl text-[#1C2826]">
            {customers.filter((c) => c.created_at && new Date(c.created_at).toDateString() === new Date().toDateString()).length}
          </div>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#738380]" />
        <Input
          placeholder="Search by name, email or phone..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-10 h-11 bg-white"
          data-testid="customer-filter-search"
        />
      </div>

      <div className="bg-white border border-[#E1E5DF] rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[#F9F9F7] text-left text-xs uppercase tracking-wider text-[#4A5D5A]">
            <tr>
              <th className="p-3">Customer</th>
              <th className="p-3">Contact</th>
              <th className="p-3">Orders</th>
              <th className="p-3">Spent</th>
              <th className="p-3">Joined</th>
              <th className="p-3">Last order</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E1E5DF]">
            {loading && <tr><td colSpan={6} className="p-8 text-center text-[#738380]">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center text-[#738380]">
                <UsersIcon className="h-6 w-6 mx-auto mb-2 opacity-50" />
                {customers.length === 0 ? "No registered customers yet" : "No customers match your search"}
              </td></tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id} data-testid={`customer-row-${c.id}`}>
                <td className="p-3">
                  <div className="font-semibold text-[#1C2826]">{c.name}</div>
                  <div className="text-xs text-[#738380]">{c.address || "—"}</div>
                </td>
                <td className="p-3 space-y-1">
                  <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-xs text-[#2F5233] hover:underline">
                    <Mail className="h-3 w-3" /> {c.email}
                  </a>
                  {c.phone && (
                    <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 text-xs text-[#2F5233] hover:underline">
                      <Phone className="h-3 w-3" /> {c.phone}
                    </a>
                  )}
                </td>
                <td className="p-3 font-semibold text-[#1C2826]">{c.order_count}</td>
                <td className="p-3 font-heading font-bold text-[#2F5233]">{peso(c.total_spent || 0)}</td>
                <td className="p-3 text-xs text-[#4A5D5A]">{c.created_at ? new Date(c.created_at).toLocaleDateString("en-PH") : "—"}</td>
                <td className="p-3 text-xs text-[#4A5D5A]">{c.last_order_at ? new Date(c.last_order_at).toLocaleDateString("en-PH") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
